import ImageViewModel from '../../view-models/ImageViewModel';
import { StepViewModel } from './types/StepViewModel';
import { mockStepsContent } from './mockStepsContent';

type RawStep = {
  stepNumber?: unknown;
  number?: unknown;
  title?: unknown;
  heading?: unknown;
  description?: unknown;
  text?: unknown;
  image?: unknown;
};

const getStepNumber = (value: unknown, fallbackValue: number) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return fallbackValue;
};

const getText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const getImage = (value: unknown): ImageViewModel | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const imageCandidate = value as Record<string, unknown>;
  const src =
    typeof imageCandidate.src === 'string'
      ? imageCandidate.src
      : typeof imageCandidate.url === 'string'
      ? imageCandidate.url
      : undefined;

  if (!src) {
    return undefined;
  }

  return {
    src,
    alt:
      typeof imageCandidate.alt === 'string' ? imageCandidate.alt : undefined,
    width:
      typeof imageCandidate.width === 'number'
        ? imageCandidate.width
        : undefined,
    height:
      typeof imageCandidate.height === 'number'
        ? imageCandidate.height
        : undefined,
    srcset:
      typeof imageCandidate.srcset === 'string'
        ? imageCandidate.srcset
        : undefined,
  };
};

const normalizeStep = (step: RawStep, index: number): StepViewModel | null => {
  const title = getText(step.title) || getText(step.heading);
  const description = getText(step.description) || getText(step.text);

  if (!title || !description) {
    return null;
  }

  return {
    stepNumber: getStepNumber(step.stepNumber ?? step.number, index + 1),
    title,
    description,
    image: getImage(step.image),
  };
};

export const resolveStepsContent = (steps: unknown): StepViewModel[] => {
  if (!Array.isArray(steps)) {
    return mockStepsContent;
  }

  const normalizedSteps = steps
    .map((step, index) => normalizeStep((step as RawStep) || {}, index))
    .filter((step): step is StepViewModel => !!step);

  return normalizedSteps.length > 0 ? normalizedSteps : mockStepsContent;
};
